// useLightbox.js — стан поточного медіа, навігація, слайдшоу

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const SCALE_MIN = 1;
const SCALE_MAX = 6;
const SCALE_STEP = 0.12;

export default function useLightbox({ images: propImages = [], videos: propVideos = [], pdfs: propPdfs = [] }) {
    // ==== Lightbox & Zoom (+ video + slideshow)
    const [current, setCurrent] = useState(null); // null | { kind:'image'|'pdf'|'video', index:number }
    const [mode, setMode] = useState('image');    // 'image' | 'video'  (PDF рендеримо у режимі image)

    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);

    // image lightbox fade-in
    const [imgReady, setImgReady] = useState(false);

    const lastPointRef = useRef({ x: 0, y: 0, t: 0 });
    const velocityRef = useRef({ vx: 0, vy: 0 });
    const rafRef = useRef(null);
    const pinchRef = useRef({ active: false, dist: 0, baseScale: 1 });

    const viewerRef = useRef(null);
    const videoRef = useRef(null);

    const [slideshow, setSlideshow] = useState(false);
    const [slideInterval, setSlideInterval] = useState(3); // seconds
    const slideTimerRef = useRef(null);

    const [rotation, setRotation] = useState(0);

    const getFullSrc = useCallback((id) => `/drive.php?action=img&id=${encodeURIComponent(id)}`, []);

    // Зберігатимемо попередні масиви тут (одноразово на сесію перегляду)
    const prevListsRef = useRef(null);

    // Якщо не null — ці списки використовуються замість пропів
    const [listsOverride, setListsOverride] = useState(null);

    const [activeLists, setActiveLists] = useState({
        images: propImages,
        videos: propVideos,
        pdfs: propPdfs,
    });

    // синхронізуємо activeLists з пропсами, коли override вимкнено
    useEffect(() => {
        if (listsOverride) return;
        setActiveLists({ images: propImages, videos: propVideos, pdfs: propPdfs });
    }, [propImages, propVideos, propPdfs, listsOverride]);

    // Єдине джерело поточних списків для всього хука
    const lists = useMemo(() => {
        if (listsOverride) return listsOverride;
        return { ...activeLists, others: [] };
    }, [listsOverride, activeLists]);

    const mediaImages = lists.images; // для слайдшоу
    const pickList = (k, sets) => (k === 'image' ? sets.images : k === 'video' ? sets.videos : k === 'pdf' ? sets.pdfs : sets.others);

    // --- helpers ---
    const mapTypeToKind = (typeOrMime = '', name = '') => {
        const t = String(typeOrMime).toLowerCase();
        const n = String(name).toLowerCase();
        const hasExt = (ext) => n.endsWith(`.${ext}`);

        if (t.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif', 'heic', 'heif'].some(hasExt)) return 'image';
        if (t === 'application/pdf' || hasExt('pdf')) return 'pdf';
        if (t.startsWith('video/') || ['mp4', 'webm', 'mov', 'm4v', 'mkv', 'avi'].some(hasExt)) return 'video';
        return 'other';
    };

    // Приводимо вузол до уніфікованого "file-like"
    const toFileLike = (nd) => ({
        id: nd.id,
        name: nd.name,
        mimeType: nd.type || nd.mimeType || '',
        type: nd.type || nd.mimeType || '',
        url: nd.url || nd.webViewLink || null,
        webViewLink: nd.webViewLink || nd.url || null,
        thumb: nd.thumb || nd.thumbnailLink || null,
        thumbnailLink: nd.thumbnailLink || nd.thumb || null,
        icon: nd.icon || nd.iconLink || null,
        iconLink: nd.iconLink || nd.icon || null,
    });

    const splitByKind = (arr = []) => {
        const images = [];
        const videos = [];
        const pdfs = [];
        const others = [];
        for (const f of arr) {
            const k = mapTypeToKind(f.type || f.mimeType, f.name);
            if (k === 'image') images.push(f);
            else if (k === 'video') videos.push(f);
            else if (k === 'pdf') pdfs.push(f);
            else others.push(f);
        }
        return { images, videos, pdfs, others };
    };

    const getViewerCenter = () => {
        const el = viewerRef.current;
        if (!el) return { cx: 0, cy: 0 };
        const r = el.getBoundingClientRect();
        return { cx: r.left + r.width / 2, cy: r.top + r.height / 2 };
    };

    const setScaleAt = useCallback((nextScale, focusX, focusY) => {
        nextScale = clamp(nextScale, SCALE_MIN, SCALE_MAX);
        setOffset((o) => {
            const { cx, cy } = getViewerCenter();
            const vx = focusX - cx - o.x;
            const vy = focusY - cy - o.y;
            const k = nextScale / scale;
            return { x: o.x - (k - 1) * vx, y: o.y - (k - 1) * vy };
        });
        setScale(nextScale);
    }, [scale]);

    const findIndexById = (list, id) => {
        const i = list.findIndex(x => String(x.id) === String(id));
        return i >= 0 ? i : 0;
    };

    const aliasKind = (k) => {
        const s = String(k || '').toLowerCase();
        if (s === 'img') return 'image';
        if (s === 'application/pdf') return 'pdf';
        return s;
    };

    const openAt = (idOrIndex, kind, nodes = []) => {
        const targetId = idOrIndex;

        if (Array.isArray(nodes) && nodes.length > 0) {
            // 0) зберігаємо поточні активні масиви один раз
            if (!prevListsRef.current) {
                prevListsRef.current = {
                    images: activeLists.images,
                    videos: activeLists.videos,
                    pdfs: activeLists.pdfs,
                };
            }

            // 1) будуємо списки САМЕ з nodes
            const transformed = nodes.map(toFileLike);
            const temp = splitByKind(transformed);

            // 2) визначаємо rawNode і нормалізуємо kind через mapTypeToKind
            const rawNode =
                transformed.find(x => String(x.id) === String(targetId)) ??
                transformed[0];

            // основний шлях — по фактичних типах rawNode
            let normalizedKind = mapTypeToKind(
                aliasKind(kind) || rawNode?.type || rawNode?.mimeType || '',
                rawNode?.name || ''
            );
            // невеличкий fallback якщо mapTypeToKind повернув 'other', а користувач дав скорочення
            if (normalizedKind === 'other') {
                const a = aliasKind(kind);
                if (a === 'image' || a === 'video' || a === 'pdf') normalizedKind = a;
            }

            // 3) індекс у відповідному списку (по ID)
            const list = pickList(normalizedKind, temp);
            const idxInList = findIndexById(list, targetId);

            // 4) підміняємо масиви, які рендеряться, і вмикаємо override
            setListsOverride(temp);
            setActiveLists({ images: temp.images, videos: temp.videos, pdfs: temp.pdfs });

            // 5) виставляємо режим/поточний
            setMode(normalizedKind === 'pdf' ? 'image' : normalizedKind);
            setCurrent({ index: idxInList, kind: normalizedKind });
        } else {
            // вихід з тимчасового перегляду — відновлюємо збережені списки
            restoreLists();
            setMode(kind);
            setCurrent({ index: targetId, kind: kind });
        }

        // ресети
        setScale(1);
        setOffset({ x: 0, y: 0 });
        setSlideshow(false);
        setRotation(0);
    };

    const restoreLists = () => {
        if (!prevListsRef.current) {
            // просто вимикаємо override, якщо він ще увімкнений
            setListsOverride(null);
            return;
        }
        // 1) повертаємо збережені масиви у рендер
        setActiveLists(prevListsRef.current);
        // 2) вимикаємо override
        setListsOverride(null);
        // 3) очищаємо снапшот
        prevListsRef.current = null;
    };


    const close = () => {
        setCurrent(null);
        setSlideshow(false);
        setRotation(0);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (slideTimerRef.current) { clearInterval(slideTimerRef.current); slideTimerRef.current = null; }
    };

    const prev = () => {
        setCurrent(c => {
            if (!c) return null;
            const list = pickList(c.kind, lists) ?? [];
            const newIndex = (c.index - 1 + list.length) % list.length;
            return { ...c, index: newIndex };
        });
    };

    const next = () => {
        setCurrent(c => {
            if (!c) return null;
            const list = pickList(c.kind, lists) ?? [];
            const newIndex = (c.index + 1) % list.length;
            return { ...c, index: newIndex };
        });
    };

    // wheel zoom (images only)
    const onWheel = useCallback((e) => {
        if (mode !== 'image') return;
        e.preventDefault();
        const delta = e.deltaY < 0 ? SCALE_STEP : -SCALE_STEP;
        const { cx, cy } = getViewerCenter();
        setScaleAt(scale + delta, cx, cy);
    }, [scale, setScaleAt, mode]);

    // panning with inertia (images only)
    const onMouseDown = (e) => {
        if (mode !== 'image' || scale === 1) return;
        setIsPanning(true);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        lastPointRef.current = { x: e.clientX, y: e.clientY, t: performance.now() };
    };
    const onMouseMove = (e) => {
        if (!isPanning) return;
        const now = performance.now();
        const dx = e.clientX - lastPointRef.current.x;
        const dy = e.clientY - lastPointRef.current.y;
        const dt = Math.max(1, now - lastPointRef.current.t);

        setOffset(o => ({ x: o.x + dx, y: o.y + dy }));
        velocityRef.current = { vx: dx / dt * 16, vy: dy / dt * 16 };
        lastPointRef.current = { x: e.clientX, y: e.clientY, t: now };
    };
    const onMouseUp = () => {
        if (!isPanning) return;
        setIsPanning(false);
        const decay = 0.92;
        const step = () => {
            const { vx, vy } = velocityRef.current;
            if (Math.hypot(vx, vy) < 0.2) { rafRef.current = null; return; }
            setOffset(o => ({ x: o.x + vx, y: o.y + vy }));
            velocityRef.current = { vx: vx * decay, vy: vy * decay };
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
    };

    const onDoubleClick = (e) => {
        if (mode !== 'image') return;
        e.stopPropagation();
        const targetScale = scale === 1 ? 2 : 1;
        const { cx, cy } = getViewerCenter();
        setScaleAt(targetScale, cx, cy);
    };

    // touch (images)
    const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const onTouchStart = (e) => {
        if (mode !== 'image') return;
        if (e.touches.length === 2) {
            pinchRef.current = { active: true, dist: dist(e.touches[0], e.touches[1]), baseScale: scale };
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        } else if (e.touches.length === 1 && scale > 1) {
            lastPointRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: performance.now() };
            setIsPanning(true);
        }
    };
    const onTouchMove = (e) => {
        if (mode !== 'image') return;
        if (pinchRef.current.active && e.touches.length === 2) {
            e.preventDefault();
            const d = dist(e.touches[0], e.touches[1]);
            const ns = clamp(pinchRef.current.baseScale * (d / pinchRef.current.dist), SCALE_MIN, SCALE_MAX);
            const fx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
            const fy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
            setScaleAt(ns, fx, fy);
        } else if (isPanning && e.touches.length === 1) {
            const now = performance.now();
            const dx = e.touches[0].clientX - lastPointRef.current.x;
            const dy = e.touches[0].clientY - lastPointRef.current.y;
            const dt = Math.max(1, now - lastPointRef.current.t);

            setOffset(o => ({ x: o.x + dx, y: o.y + dy }));
            velocityRef.current = { vx: dx / dt * 16, vy: dy / dt * 16 };
            lastPointRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: now };
        }
    };
    const onTouchEnd = () => {
        if (mode !== 'image') return;
        pinchRef.current.active = false;
        if (!isPanning) return;
        setIsPanning(false);
        const decay = 0.92;
        const step = () => {
            const { vx, vy } = velocityRef.current;
            if (Math.hypot(vx, vy) < 0.2) { rafRef.current = null; return; }
            setOffset(o => ({ x: o.x + vx, y: o.y + vy }));
            velocityRef.current = { vx: vx * decay, vy: vy * decay };
            rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
    };

    // Відео: базові хендлери
    useEffect(() => {
        const v = videoRef.current;
        if (!v || mode !== 'video' || current == null) return;

        const onLoadedMetadata = () => { };
        const onCanPlay = () => { };

        v.addEventListener('loadedmetadata', onLoadedMetadata);
        v.addEventListener('canplay', onCanPlay);
        return () => {
            v.removeEventListener('loadedmetadata', onLoadedMetadata);
            v.removeEventListener('canplay', onCanPlay);
        };
    }, [mode, current]);

    // коли перемикаємося на інше зображення — скидаємо трансформації
    useEffect(() => {
        if (mode !== 'image') return;
        if (current == null) return;
        setScale(1);
        setOffset({ x: 0, y: 0 });
        setImgReady(false);
    }, [mode, current]);

    // колесо/тач — лише для зображень
    useEffect(() => {
        const el = viewerRef.current;
        if (!el || mode !== 'image') return;
        const wheelHandler = (e) => onWheel(e);
        const touchMoveHandler = (e) => { if (pinchRef.current.active) e.preventDefault(); };
        el.addEventListener('wheel', wheelHandler, { passive: false });
        el.addEventListener('touchmove', touchMoveHandler, { passive: false });
        return () => {
            el.removeEventListener('wheel', wheelHandler);
            el.removeEventListener('touchmove', touchMoveHandler);
        };
    }, [onWheel, mode]);

    // невеликий воркараунд для "засипання" стріму
    useEffect(() => {
        const v = videoRef.current;
        if (!v || mode !== 'video' || current == null) return;

        let lastTime = 0;
        let lastUpdate = performance.now();
        let interval;

        const check = () => {
            if (!v || v.paused) return;
            const now = performance.now();

            if (Math.abs(v.currentTime - lastTime) < 0.05) {
                if (now - lastUpdate > 1500) {
                    try { v.currentTime = v.currentTime + 0.001; lastUpdate = now; } catch { /*  */ }
                }
            } else {
                lastTime = v.currentTime;
                lastUpdate = now;
            }
        };

        interval = setInterval(check, 500);
        return () => clearInterval(interval);
    }, [mode, current]);

    // keyboard shortcuts when lightbox open
    useEffect(() => {
        if (current === null) return;
        const onKey = (e) => {
            if (e.key === 'Escape') { close(); return; }
            if (e.key === 'ArrowLeft') { prev(); return; }
            if (e.key === 'ArrowRight') { next(); return; }
            if (mode === 'image') {
                if (e.key === '+' || e.key === '=') { const { cx, cy } = getViewerCenter(); setScaleAt(scale + SCALE_STEP, cx, cy); }
                if (e.key === '-') { const { cx, cy } = getViewerCenter(); setScaleAt(scale - SCALE_STEP, cx, cy); }
                if (e.key === '0') { const { cx, cy } = getViewerCenter(); setScaleAt(1, cx, cy); }
                if (e.key.toLowerCase() === 'f') { setScale(1); setOffset({ x: 0, y: 0 }); }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [current, mode, scale, setScaleAt]);

    const zoomIn = () => { const { cx, cy } = getViewerCenter(); setScaleAt(scale + SCALE_STEP, cx, cy); };
    const zoomOut = () => { const { cx, cy } = getViewerCenter(); setScaleAt(scale - SCALE_STEP, cx, cy); };
    const zoomFit = () => { setScale(1); setOffset({ x: 0, y: 0 }); };
    const zoom100 = () => { const { cx, cy } = getViewerCenter(); setScaleAt(1, cx, cy); };

    // ===== Slideshow (images)
    const wait = (ms) => new Promise(res => setTimeout(res, ms));

    const waitImageLoaded = (src) => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = src;
        if (img.complete) resolve(true);
    });

    const waitUntil = async (cond, timeoutMs = 2000, step = 20) => {
        const t0 = performance.now();
        while (!cond()) {
            if (performance.now() - t0 > timeoutMs) break;
            await wait(step);
        }
    };

    useEffect(() => {
        if (mode !== 'image') return;
        if (!slideshow) return;
        if (mediaImages.length === 0) return;

        let cancelled = false;
        (async () => {
            let idx = current?.index ?? 0;

            while (!cancelled && slideshow && mode === 'image' && mediaImages.length > 0) {
                const file = mediaImages[idx];
                const src = getFullSrc(file.id);

                await waitImageLoaded(src);
                if (cancelled) break;

                setCurrent({ index: idx, kind: 'image' });
                setImgReady(false);

                await waitUntil(() => imgReady === true, 2000, 20);
                if (cancelled) break;

                await wait(Math.max(1, slideInterval) * 1000);
                if (cancelled) break;

                idx = (idx + 1) % mediaImages.length;
            }
        })();

        return () => { cancelled = true; };
    }, [slideshow, slideInterval, mode, mediaImages, getFullSrc]); // imgReady не додаємо спеціально

    return {
        current, openAt, close, prev, next,
        offset, rotation, setSlideshow, setSlideInterval, setRotation,
        viewerRef, videoRef,
        scale, onMouseDown, onMouseMove, onMouseUp,
        onDoubleClick, onTouchStart, onTouchMove, onTouchEnd,
        zoomIn, zoomOut, zoomFit, zoom100,
        setImgReady, imgReady,

        // 🔹 віддай назовні активні масиви — саме їх треба рендерити
        viewImages: activeLists.images,
        viewVideos: activeLists.videos,
        viewPdfs: activeLists.pdfs,
    };
}
